import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { SEO } from "@/components/SEO";
import {
  Shield,
  Target,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Lock,
  Clock,
  Zap,
  Brain,
} from "lucide-react";
import {
  HeroSection,
  LogoCloud,
  StatsCounter,
  BeforeAfterDemo,
  UseCaseCards,
  TestimonialCards,
  FAQSection,
  ROICalculator,
  ScrollAnimatedSection,
} from "@/components/landing";

const features = [
  {
    icon: Target,
    title: "Clarity Scoring",
    description: "Every requirement gets a clarity score so you know what needs attention.",
  },
  {
    icon: Shield,
    title: "Privacy First",
    description: "Temporary mode available. Enterprise supports zero-retention execution.",
  },
  {
    icon: Sparkles,
    title: "Jira Integration",
    description: "Export improved requirements directly to Jira with the reqqy-reviewed label.",
  },
];

const securityFeatures = [
  "Data stored by default for convenience",
  "Temporary (ephemeral) mode available",
  "Enterprise zero-retention execution",
  "We do not train on customer data",
  "We do not reuse customer content",
  "SOC 2 Type II compliant infrastructure",
];

const howItWorksSteps = [
  {
    step: "01",
    title: "Paste requirements",
    desc: "User stories, PRD excerpts, bullet notes — anything.",
    time: "30 seconds",
  },
  { step: "02", title: "Add context", desc: "Optional: constraints, success metrics, personas.", time: "Optional" },
  { step: "03", title: "Run Reqqy", desc: "Quick or deep analysis — you choose.", time: "1-2 min" },
  { step: "04", title: "Get clarity", desc: "See what's unclear and accept improvements.", time: "Instant" },
];

export default function Landing() {
  return (
    <PublicLayout>
      <SEO
        canonical="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Reqqy",
          applicationCategory: "BusinessApplication",
          description: "Reqqy is an AI-powered requirements clarity and ambiguity detection tool for product teams.",
          url: "https://reqqy.app",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }}
      />

      {/* Hero Section */}
      <HeroSection />

      {/* Logo Cloud */}
      <LogoCloud />

      {/* Stats Counter */}
      <StatsCounter />

      {/* How It Works */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        {/* Layered background */}
        <div className="absolute inset-0 bg-card/30" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute top-[-10%] left-[30%] w-[500px] h-[400px] rounded-full bg-primary/[0.05] blur-[140px]" />
        <div className="absolute bottom-[-10%] right-[20%] w-[400px] h-[300px] rounded-full bg-accent/[0.04] blur-[120px]" />

        <div className="container relative z-10 px-4 md:px-6">
          <ScrollAnimatedSection>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                How it works
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Clear requirements in minutes</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Paste what you would normally send to engineering. Reqqy reviews it and tells you what's unclear.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6">
              {howItWorksSteps.map((item, index) => (
                <div key={item.step} className="relative group">
                  <div className="relative glass p-6 rounded-xl border border-border/30 h-full transition-all duration-500 hover:border-primary/20 hover:shadow-[0_8px_40px_-12px_hsl(var(--primary)/0.12)] hover:-translate-y-1">
                    {/* Hover gradient */}
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-primary/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="relative z-10">
                      <div className="text-5xl font-display font-bold text-primary/20 mb-4 transition-all duration-500 group-hover:text-primary/50 group-hover:scale-105 origin-left">
                        {item.step}
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{item.desc}</p>
                      <div className="inline-flex items-center gap-1 text-xs text-primary bg-primary/10 px-2.5 py-1 rounded-md">
                        <Clock className="w-3 h-3" />
                        {item.time}
                      </div>
                    </div>
                  </div>
                  {index < 3 && (
                    <ArrowRight className="hidden md:block absolute top-8 -right-4 z-10 w-5 h-5 text-primary/30" />
                  )}
                </div>
              ))}
            </div>
          </ScrollAnimatedSection>
        </div>
      </section>

      {/* Before/After Demo */}
      <ScrollAnimatedSection>
        <BeforeAfterDemo />
      </ScrollAnimatedSection>

      {/* ROI Calculator */}
      <ROICalculator />

      {/* Use Case Cards */}
      <ScrollAnimatedSection>
        <UseCaseCards />
      </ScrollAnimatedSection>

      {/* Testimonials */}
      <ScrollAnimatedSection>
        <TestimonialCards />
      </ScrollAnimatedSection>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-card/20" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute top-[20%] right-[15%] w-[500px] h-[500px] rounded-full bg-primary/[0.05] blur-[160px]" />
        <div className="absolute bottom-[10%] left-[10%] w-[400px] h-[400px] rounded-full bg-accent/[0.04] blur-[140px]" />

        <div className="container relative z-10 px-4 md:px-6">
          <ScrollAnimatedSection>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Features
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Built for product teams</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Reqqy helps you delegate the tedious work of reviewing requirements while staying in control.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className="group relative p-6 md:p-8 rounded-xl glass border border-border/30 animate-fade-in transition-all duration-500 hover:border-primary/20 hover:shadow-[0_8px_40px_-12px_hsl(var(--primary)/0.12)] hover:-translate-y-1"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Hover gradient */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-primary/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-6 transition-all duration-300 group-hover:shadow-[0_0_24px_-4px_hsl(var(--primary)/0.3)] group-hover:scale-105">
                      <feature.icon className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollAnimatedSection>
        </div>
      </section>

      {/* Enterprise Ready */}
      <section className="py-20 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-card/30" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute top-[10%] left-[20%] w-[600px] h-[500px] rounded-full bg-primary/[0.04] blur-[160px]" />
        <div className="absolute bottom-[20%] right-[10%] w-[400px] h-[400px] rounded-full bg-accent/[0.03] blur-[140px]" />

        <div className="container relative z-10 px-4 md:px-6">
          <ScrollAnimatedSection>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                  <Shield className="w-4 h-4" />
                  Enterprise Ready
                </div>
                <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">Security and privacy built in</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Reqqy is designed with enterprise security requirements in mind. Your data stays private.
                </p>
                <ul className="space-y-3">
                  {securityFeatures.map((feature) => (
                    <li key={feature} className="group flex items-center gap-3 p-2 -ml-2 rounded-lg transition-colors duration-300 hover:bg-primary/[0.03]">
                      <CheckCircle2 className="w-5 h-5 text-reqqy-success flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <Button asChild className="gradient-primary text-primary-foreground hover:opacity-90 transition-all duration-300 hover:shadow-[0_0_30px_-8px_hsl(var(--primary)/0.4)]">
                    <Link to="/pricing">
                      View Enterprise Plans
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="border-border/60 hover:border-primary/30 transition-all duration-300">
                    <Link to="/contact-sales">Book a Demo</Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-2xl glass p-8 relative overflow-hidden border border-border/30 transition-all duration-500 hover:border-primary/15 hover:shadow-[0_8px_60px_-16px_hsl(var(--primary)/0.1)]">
                  {/* Inner ambient glow */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[200px] rounded-full bg-primary/[0.06] blur-[80px]" />

                  <div className="relative z-10 flex flex-col items-center justify-center gap-8 py-6">
                    <div className="w-20 h-20 rounded-2xl gradient-primary flex items-center justify-center glow-primary transition-transform duration-500 hover:scale-105">
                      <Lock className="w-10 h-10 text-primary-foreground" />
                    </div>
                    <div className="text-center">
                      <div className="text-5xl font-display font-bold mb-1">99.99%</div>
                      <div className="text-muted-foreground text-sm">Uptime SLA</div>
                    </div>
                    <div className="flex gap-8">
                      <div className="text-center group">
                        <div className="text-2xl font-bold transition-colors duration-300 group-hover:text-primary">Zero</div>
                        <div className="text-xs text-muted-foreground">Training on Data</div>
                      </div>
                      <div className="w-px bg-border/60" />
                      <div className="text-center group">
                        <div className="text-2xl font-bold transition-colors duration-300 group-hover:text-primary">SOC 2</div>
                        <div className="text-xs text-muted-foreground">Type II</div>
                      </div>
                    </div>
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      {["GDPR", "CCPA", "HIPAA Ready"].map((badge) => (
                        <span
                          key={badge}
                          className="px-3 py-1.5 rounded-lg bg-background/50 border border-border/30 font-medium transition-all duration-300 hover:border-primary/20 hover:text-foreground"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollAnimatedSection>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section — Peak-End: most dramatic, memorable moment */}
      <section className="py-28 md:py-40 relative overflow-hidden">
        {/* Intense layered background for peak-end */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-card/50" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/[0.12] rounded-full blur-[160px] animate-pulse-slow" />
        <div className="absolute bottom-[10%] left-[20%] w-[400px] h-[400px] bg-accent/[0.08] rounded-full blur-[140px] animate-pulse-slow" style={{ animationDelay: "2s" }} />
        <div className="absolute top-[40%] right-[10%] w-[300px] h-[300px] bg-primary/[0.06] rounded-full blur-[120px] animate-pulse-slow" style={{ animationDelay: "4s" }} />

        {/* Noise texture */}
        <div className="absolute inset-0 opacity-[0.025] mix-blend-overlay" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }} />

        {/* Grid lines */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary) / 0.5) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.5) 1px, transparent 1px)`,
            backgroundSize: "80px 80px",
          }}
        />

        {/* Top edge line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <div className="container relative z-10 px-4 md:px-6 text-center">
          <ScrollAnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20 text-sm mb-10">
              <Brain className="w-4 h-4 text-primary" />
              <span className="text-muted-foreground">AI-powered requirements review</span>
            </div>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold mb-6 leading-[1.05] tracking-[-0.02em]">
              Ready for <span className="text-gradient">clearer</span> requirements?
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto mb-12 leading-relaxed">
              Stop sending ambiguous requirements to engineering. Let Reqqy help you get it right the first time.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="relative group h-12 px-8 text-lg font-semibold gradient-primary text-primary-foreground overflow-hidden transition-all duration-300 hover:shadow-[0_0_50px_-8px_hsl(var(--primary)/0.5)]"
              >
                <Link to="/dashboard/new-run?mode=review">
                  <span className="relative z-10 flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Start reviewing — free
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  {/* Shimmer sweep */}
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-8 text-lg border-border/60 hover:border-primary/40 transition-all duration-300">
                <Link to="/contact-sales">Talk to Sales</Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground/70 mt-6 tracking-wide">
              No credit card required · 10 free credits · Setup in 30 seconds
            </p>
          </ScrollAnimatedSection>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </section>
    </PublicLayout>
  );
}
